import { Dialog, DialogConfig } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { Router } from '@angular/router';
import { AuthGuard } from '@core/authentication/auth.guard';
import { AuthService } from '@core/authentication/auth.service';
import moment from 'moment';
import { OiqLayoutNotificationDialogComponent } from '../notifications/notification-dialog.component';
import { OiqNotificationSmsAlertsListComponent } from '../sms-alerts/sms-alerts-list.component';
import { OiqSmsAlert } from '../sms-alerts/sms-alerts.service';
import { OiqSmsAlertsStore } from '../sms-alerts/sms-alerts.store';
import { OiqNotificationEventsListComponent } from './events-list.component';
import { OiqNotificationEventsService } from './events.service';
import { OiqNotificationEventsStore } from './events.store';

@Component({
  selector: 'oiq-notification-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
  imports: [AsyncPipe, OiqNotificationEventsListComponent, OiqNotificationSmsAlertsListComponent],
})
export class OiqNotificationEventsComponent implements OnInit, AfterViewInit {
  @Output() event = new EventEmitter<{ type: string; data?: any }>();

  public dialog = inject(Dialog);
  private calendarService = inject(OiqNotificationEventsService);
  private authService = inject(AuthService);
  private authGuard = inject(AuthGuard);
  private router = inject(Router);
  public store = inject(OiqNotificationEventsStore);
  public smsAlertsStore = inject(OiqSmsAlertsStore);

  background: ThemePalette = undefined;
  isDeleting: boolean;

  activeTab: 'upcoming' | 'past' | 'future' | 'sms' = 'upcoming';

  constructor() {}

  ngAfterViewInit(): void {
    this.store.reload$.next();
    this.smsAlertsStore.reload$.next();
  }

  ngOnInit() {}

  handleEvent(event$: { type: string; data: any }): void {
    switch (event$.type) {
      case 'edit':
        this.openEditCalendarEventDialog(event$.data);
        break;
      case 'delete':
        this.deleteEvent(event$.data);
        break;
      case 'show':
        this.event.emit({ type: 'close' });
        break;
      default:
        break;
    }
  }

  /** Users who cannot open fundings (super admins, syndicators) only get mark-as-read on SMS rows. */
  canOpenSmsAlerts = this.authGuard.canAccess('fundings');

  handleSmsAlertEvent(event$: { type: 'open' | 'read'; data: OiqSmsAlert }): void {
    const _alert = event$.data;
    if (event$.type === 'read') {
      this.smsAlertsStore.markRead([_alert.smsMessageId]);
      return;
    }
    this.event.emit({ type: 'close' });
    // Only mark read once the funding opened: the auth guard denies users without fundings access.
    this.router
      .navigate(['/fundings', _alert.fundingId, 'notifications'], {
        queryParams: { tab: 'sms', contact: _alert.contactId, message: _alert.smsMessageId },
      })
      .then(_navigated => {
        if (_navigated) {
          this.smsAlertsStore.markRead([_alert.smsMessageId]);
        }
      });
  }

  markAllSmsAlertsRead(alerts: OiqSmsAlert[]): void {
    this.smsAlertsStore.markRead(alerts.map(alert => alert.smsMessageId));
  }

  deleteEventReminder(calendarEventId: number, id: number) {
    this.calendarService.deleteCalendarReminder(calendarEventId, id).subscribe();
  }

  openEditCalendarEventDialog(row?: any, setRead?: boolean): void {
    const dialogConfig: DialogConfig<any, any, any> = {};
    dialogConfig.width = '860px';
    dialogConfig.data = {};
    const now = Date.now().toString();
    if (row.id) {
      dialogConfig.data = row;

      if (!row.eventRead && setRead) {
        this.calendarService.toggleEventRead(row.id).subscribe(r => {
          dialogConfig.data.eventRead = true;
        });
      }
    }
    dialogConfig.data.user = this.authService.getUser();
    const dialogRef = this.dialog.open(OiqLayoutNotificationDialogComponent, dialogConfig);
    dialogRef.closed.subscribe(closeNotifications => {
      this.store.reload$.next();
      if (closeNotifications) {
        this.event.emit({ type: 'close' });
      }
    });
  }

  deleteEvent(item) {
    this.isDeleting = true;
    item.calendarReminders.forEach(reminder => {
      this.deleteEventReminder(item.id, reminder.id);
    });

    this.calendarService.delete(item.id).subscribe((r: any) => {
      this.store.reload$.next();
      this.isDeleting = false;
    });
  }

  getFormatFromNow(datetime) {
    return moment(datetime).fromNow();
  }
}
